import { useState, useEffect, useRef } from 'react';
import {
  showSuccess,
  showError,
  showLoading,
  dismissToast,
  showConfirmToast,
} from '../components/Ui/ToastNotification';
import useSupportApi from './useSupportApi';
import api from '../services/Api';
import { useSelector } from 'react-redux';

export const useSupportTicket = (userData) => {
  const { userData: user } = useSelector((state) => state.auth);
  const userEmail = user?.email;
  // State management
  const [message, setMessage] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(null);

  // Refs
  const fileInputRef = useRef(null);
  const moreMenuRef = useRef(null);

  // API hooks
  const {
    loading,
    error,
    fetchTickets,
    getTicketById,
    replyToTicket,
    deleteTicket,
    updateTicketStatus,
  } = useSupportApi();

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      const loadTickets = async () => {
        const fetchedTickets = await fetchTickets();
        setTickets(fetchedTickets);
        setFilteredTickets(fetchedTickets);
      };
      loadTickets();
    }
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredTickets(tickets);
    } else {
      const filtered = tickets.filter(
        (ticket) => ticket.status === activeFilter,
      );
      setFilteredTickets(filtered);

      if (
        selectedTicket &&
        !filtered.some((ticket) => ticket._id === selectedTicket._id)
      ) {
        setSelectedTicket(null);
      }
    }
  }, [activeFilter, tickets, selectedTicket]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ticket operations
  const handleSelectTicket = async (ticketId) => {
    try {
      if (!ticketId) {
        console.error('No ticket ID provided');
        return;
      }

      const ticket = await getTicketById(ticketId);
      if (ticket) {
        setSelectedTicket(ticket);

        if (!ticket.isRead) {
          await api.patch(`/support/${ticketId}`, { isRead: true });
          setTickets((prevTickets) =>
            prevTickets.map((t) =>
              t._id === ticketId ? { ...t, isRead: true } : t,
            ),
          );
        }
      } else {
        console.error('No ticket found with ID:', ticketId);
      }
    } catch (error) {
      console.error('Error selecting ticket:', error);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        showError('File size should be less than 5MB');
        return;
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(selectedFile.type)) {
        showError('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      setFile(selectedFile);
      showSuccess('File selected');
    } else {
      showError('Please select a valid file');
    }
  };

  const handleUploadFile = async () => {
    if (!file) return null;

    const loadingToastId = showLoading('Uploading file...');

    try {
      const fileFormData = new FormData();
      fileFormData.append('file', file);

      const fileResponse = await api.post(`/upload/file`, fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (fileResponse.data?.url) {
        dismissToast(loadingToastId);
        showSuccess('File uploaded successfully');
        return fileResponse.data.url;
      }
      throw new Error('File upload failed');
    } catch (error) {
      dismissToast(loadingToastId);
      showError('File upload failed. Please try again.');
      console.error('File upload error:', error.response?.data || error);
      return null;
    }
  };

  const handleSendReply = async () => {
    if (!selectedTicket) {
      showError('Please select a ticket first');
      return;
    }

    if (!message.trim() && !file) {
      showError('Message or file cannot be empty');
      return;
    }

    const replyId =
      selectedTicket.businessId?._id ||
      selectedTicket.userId?._id ||
      selectedTicket.businessId ||
      selectedTicket.userId;

    if (!replyId) {
      showError('No business or user ID found');
      return;
    }

    const loadingToastId = showLoading('Sending reply...');

    try {
      let fileUrl = '';
      if (file) {
        fileUrl = await handleUploadFile();
        if (!fileUrl) {
          dismissToast(loadingToastId);
          return;
        }
      }

      const replyData = {
        userId: replyId,
        subject: `Re: ${selectedTicket.subject || 'No Subject'}`,
        message: message || 'Image',
        file: fileUrl,
      };

      const success = await replyToTicket(
        selectedTicket._id,
        replyData,
        userData.sub,
      );

      if (success) {
        dismissToast(loadingToastId);
        showSuccess('Reply sent successfully');
        setMessage('');
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        const updatedTicket = await getTicketById(selectedTicket._id);
        if (updatedTicket) {
          setSelectedTicket(updatedTicket);
        }
      } else {
        dismissToast(loadingToastId);
        showError('Failed to send reply');
      }
    } catch (error) {
      dismissToast(loadingToastId);
      showError('An error occurred while sending the reply');
      console.error('Error sending reply:', error);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  const handleStatusChange = async (ticketId, status) => {
    if (!ticketId) {
      showError('No ticket selected');
      return;
    }

    const loadingToastId = showLoading('Updating ticket status...');

    try {
      const success = await updateTicketStatus(ticketId, status);
      if (success) {
        dismissToast(loadingToastId);
        showSuccess(`Ticket status updated to ${status}`);

        const fetchedTickets = await fetchTickets();
        setTickets(fetchedTickets);

        if (selectedTicket && selectedTicket._id === ticketId) {
          const updatedTicket = await getTicketById(ticketId);
          if (updatedTicket) {
            setSelectedTicket(updatedTicket);
          }
        }
      } else {
        dismissToast(loadingToastId);
        showError('Failed to update ticket status');
      }
    } catch (error) {
      dismissToast(loadingToastId);
      showError('Error updating ticket status');
      console.error('Error updating ticket status:', error);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!ticketId) {
      showError('No ticket selected');
      return;
    }

    showConfirmToast({
      message:
        'Are you sure you want to delete this ticket? This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        const loadingToastId = showLoading('Deleting ticket...');

        try {
          const success = await deleteTicket(ticketId);
          if (success) {
            dismissToast(loadingToastId);
            showSuccess('Ticket deleted successfully');
            setTickets(tickets.filter((ticket) => ticket._id !== ticketId));

            if (selectedTicket && selectedTicket._id === ticketId) {
              setSelectedTicket(null);
            }
          } else {
            dismissToast(loadingToastId);
            showError('Failed to delete ticket');
          }
        } catch (error) {
          dismissToast(loadingToastId);
          showError('Error deleting ticket');
          console.error('Error deleting ticket:', error);
        }
      },
    });
  };

  const toggleMoreMenu = (messageId) => {
    setShowMoreMenu((prevId) => (prevId === messageId ? null : messageId));
  };

  return {
    // State
    message,
    selectedTicket,
    filteredTickets,
    activeFilter,
    file,
    uploading,
    showMoreMenu,
    loading,
    error,

    // Refs
    fileInputRef,
    moreMenuRef,

    // Handlers
    setMessage,
    handleSelectTicket,
    handleFileChange,
    handleSendReply,
    handleFilterChange,
    handleStatusChange,
    handleDeleteTicket,
    toggleMoreMenu,

    // State setters
    setFile,
    setShowMoreMenu,
  };
};
